import {
    css,
    IObjectWithKey,
    ISelection,
    MarqueeSelection,
    Selection,
    SelectionZone,
    Stack,
    StackItem,
} from '@fluentui/react'
import { AccordionPanel, makeStyles, Switch, SwitchOnChangeData, Tooltip } from '@fluentui/react-components'
import LineItemSelector from '../../LineItemSelector'
import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import { createLineItemMap } from '../../LineItemGroups'
import { LineItemGroupDialogProps } from './LineItemGroupDialog'
import { LineItemEntity } from '../../../../../../hooks/finance/invoice/useLineItemEntities'
import useVatTypes from '../../../../../../hooks/useVatTypes'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles({
    lineItemSelector: { marginLeft: '24px' },
})

export default function LineItemPanel(props: LineItemGroupDialogProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { getVatType } = useVatTypes()
    const availableEntities = useMemo(
        () =>
            createLineItemMap(
                [
                    ...[...props.lineItemEntities.orphans.values()].flatMap(entities =>
                        entities.map(entity => entity.id),
                    ),
                    ...props.values.lineItems.map(lineItem => lineItem.entityId),
                ],
                props.meta.data,
                true,
            ),
        [props.lineItemEntities.orphans, props.lineItemGroup.lineItems],
    )
    const setPropertyRef = useRef(props.setProperty)

    const selection = useMemo<ISelection<LineItemEntity & IObjectWithKey>>(() => {
        return new Selection({
            items: [...availableEntities.values()].flatMap(entities => entities),
            // The onSelectionChanged function is defined only once, when the Selection object is first created, and it
            // captures the setProperty variable as it exists at that time.
            onSelectionChanged: () =>
                setPropertyRef.current(
                    'lineItems',
                    selection
                        .getSelection()
                        .map(item => props.lineItemEntities.map(item))
                        .filter(Boolean),
                ),
            getKey: entity => entity.id,
        })
    }, [availableEntities])

    useEffect(() => {
        // Keep track of the latest setProperty, because otherwise onSelectionChanged stick to the initial state
        // of setProperty and use outdated values.
        setPropertyRef.current = props.setProperty
    }, [props.values])

    useEffect(() => {
        selection.setChangeEvents(false, true)
        props.values.lineItems.forEach(lineItem => {
            return selection.setKeySelected(lineItem.entityId, true, false)
        })
        selection.setChangeEvents(true, false)
    }, [props.values.lineItems])

    return (
        <AccordionPanel>
            <MarqueeSelection selection={selection} isDraggingConstrainedToRoot>
                <SelectionZone selection={selection} toggleWithoutModifierPressed selectionPreservedOnEmptyClick>
                    <Stack>
                        <Tooltip
                            relationship="description"
                            content={translate('invoice.edit.form.invoicegroup.general.showstatement.tooltip')}
                            withArrow
                        >
                            <Switch
                                checked={props.values.showStatement}
                                onChange={(_, data: SwitchOnChangeData) =>
                                    props.setProperty('showStatement', data.checked)
                                }
                                label={translate('invoice.edit.form.invoicegroup.general.showstatement')}
                            />
                        </Tooltip>
                        <StackItem className={css(styles.lineItemSelector)}>
                            <LineItemSelector
                                entities={availableEntities}
                                selection={selection}
                                defaultVat={getVatType(props.defaultVatType)}
                                getPrice={props.lineItemEntities.getPrice}
                                showPrice
                            />
                        </StackItem>
                    </Stack>
                </SelectionZone>
            </MarqueeSelection>
        </AccordionPanel>
    )
}
